import { html, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { fetch } from "@/utilities";

type AlertState = "none" | "info" | "warning";

const INFO_WINDOW_MS = 5 * 60 * 1000;
const NEAR_DEADLINE_WINDOW_MS = 15 * 60 * 1000;
const NEAR_POLL_INTERVAL_MS = 60 * 1000;
const FAR_POLL_INTERVAL_MS = 5 * 60 * 1000;
const TICK_INTERVAL_MS = 5000;
const MIN_FETCH_SPACING_MS = 10 * 1000;
const FETCH_TIMEOUT_MS = 10 * 1000;

/**
 * Shows an alert when the series deadline is less than 5 minutes away or has
 * passed. Polls a lightweight endpoint so the alerts stay correct when the
 * teacher changes the deadline after the page was loaded, and uses the server
 * time from that endpoint so a skewed client clock cannot shift the alerts.
 * Re-syncs immediately when the tab becomes visible again, because browsers
 * throttle timers in background tabs.
 *
 * @element d-deadline-alert
 *
 * @prop {string} url - Endpoint returning { deadline, deadline_label, exam, server_time }.
 * @prop {string} deadline - Deadline as an ISO 8601 string, may be empty.
 * @prop {string} deadlineLabel - Deadline formatted by the server in the user's profile time zone.
 * @prop {boolean} exam - Whether the series is an exam, as "true"/"false".
 * @prop {string} serverTime - Server time at page render as an ISO 8601 string.
 */
@customElement("d-deadline-alert")
export class DeadlineAlert extends DodonaElement {
    @property({ type: String }) url = "";
    @property({ type: String }) deadline = "";
    @property({ attribute: "deadline-label", type: String }) deadlineLabel = "";
    @property({
        converter: { fromAttribute: (value: string | null): boolean => value === "true" },
    })
    exam = false;
    @property({ attribute: "server-time", type: String }) serverTime = "";

    @state() private alertState: AlertState = "none";

    private clockOffset = 0;
    private lastFetchAt = Date.now();
    private fetching = false;
    private tickIntervalId: ReturnType<typeof setInterval> | undefined;

    private readonly resync = (): void => {
        if (document.hidden) {
            return;
        }
        this.alertState = this.computeAlertState();
        if (Date.now() - this.lastFetchAt >= MIN_FETCH_SPACING_MS) {
            void this.fetchDeadline();
        }
    };

    connectedCallback(): void {
        super.connectedCallback();
        this.tickIntervalId = setInterval(() => this.tick(), TICK_INTERVAL_MS);
        document.addEventListener("visibilitychange", this.resync);
        window.addEventListener("online", this.resync);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        clearInterval(this.tickIntervalId);
        this.tickIntervalId = undefined;
        document.removeEventListener("visibilitychange", this.resync);
        window.removeEventListener("online", this.resync);
    }

    protected willUpdate(changed: PropertyValues): void {
        if (changed.has("serverTime") && this.serverTime) {
            this.clockOffset = new Date(this.serverTime).getTime() - Date.now();
        }
        if (changed.has("deadline") || changed.has("serverTime")) {
            this.alertState = this.computeAlertState();
        }
    }

    private get now(): number {
        return Date.now() + this.clockOffset;
    }

    private tick(): void {
        // A hidden tab is resynced by the visibilitychange listener when it is
        // shown again, so ticking in the background would be wasted work.
        if (document.hidden) {
            return;
        }
        this.alertState = this.computeAlertState();
        if (Date.now() - this.lastFetchAt >= this.pollInterval()) {
            void this.fetchDeadline();
        }
    }

    private computeAlertState(): AlertState {
        if (!this.deadline) {
            return "none";
        }
        const deadline = new Date(this.deadline).getTime();
        if (deadline <= this.now) {
            return "warning";
        }
        if (deadline - this.now <= INFO_WINDOW_MS) {
            return "info";
        }
        return "none";
    }

    private pollInterval(): number {
        if (!this.deadline) {
            return FAR_POLL_INTERVAL_MS;
        }
        const untilDeadline = Math.abs(new Date(this.deadline).getTime() - this.now);
        return untilDeadline <= NEAR_DEADLINE_WINDOW_MS ? NEAR_POLL_INTERVAL_MS : FAR_POLL_INTERVAL_MS;
    }

    private async fetchDeadline(): Promise<void> {
        if (this.fetching || !this.url) {
            return;
        }
        this.fetching = true;
        this.lastFetchAt = Date.now();
        try {
            const response = await fetch(this.url, {
                headers: { accept: "application/json" },
                signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
            });
            const data = await response.json();
            this.clockOffset = new Date(data.server_time).getTime() - Date.now();
            this.deadline = data.deadline ?? "";
            this.deadlineLabel = data.deadline_label ?? "";
            this.exam = Boolean(data.exam);
            this.alertState = this.computeAlertState();
        } catch {
            // Keep the last known deadline if a poll fails; the next scheduled
            // poll (after pollInterval has elapsed again) retries.
        } finally {
            this.fetching = false;
        }
    }

    // i18n-tasks-use t('js.deadline_alert.in_5_minutes') t('js.deadline_alert.passed') t('js.deadline_alert.passed_exam')
    render(): TemplateResult {
        if (this.alertState === "none") {
            return html``;
        }
        if (this.alertState === "info") {
            return html`<div class="alert alert-info mt-3">
                ${I18n.t("js.deadline_alert.in_5_minutes", { deadline: this.deadlineLabel })}
            </div>`;
        }
        const key = this.exam ? "js.deadline_alert.passed_exam" : "js.deadline_alert.passed";
        return html`<div class="alert alert-warning mt-3">${I18n.t(key, { deadline: this.deadlineLabel })}</div>`;
    }
}
